import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  Collapse,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  LinearProgress,
} from '@material-ui/core';
import {
  DirectionsCar as CarIcon,
  Build as BuildIcon,
  History as HistoryIcon,
  Pageview,
  Warning as WarningIcon,
  LocalShipping as TruckIcon,
  Speed as SpeedIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
} from '@material-ui/icons';
import { format, differenceInDays } from 'date-fns';
import { Link } from 'react-router-dom'

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    flexGrow: 1,
  },
  searchContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  searchField: {
    marginRight: theme.spacing(2),
  },
  statsCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  statsIcon: {
    width: 40,
    height: 40,
  },
  cardContent: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  table: {
    minWidth: 650,
  },
  expandCell: {
    paddingBottom: 0,
    paddingTop: 0,
  },
  expandedContent: {
    backgroundColor: theme.palette.background.default,
  },
  vehicleRow: {
    '& > *': {
      borderBottom: 'unset',
    },
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  statusChip: {
    margin: theme.spacing(0.5),
    fontWeight: 'bold',
  },
  goodStatus: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  warningStatus: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  criticalStatus: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  tabContent: {
    padding: theme.spacing(2),
    minHeight: 200,
  },
  listItem: {
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  mileageProgress: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  detailSection: {
    marginBottom: theme.spacing(2),
  },
  vehicleIcon: {
    fontSize: 60,
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  costSummary: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  noData: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  urgentBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
    },
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  priorityRow: {
    backgroundColor: theme.palette.warning.light + '20',
  },
  detailsText: {
    fontWeight: '500',
    fontSize: 14,
    color: theme.palette.primary.main,
  }
}));

const FleetOverview = ({ url, user }) => {
  const classes = useStyles();
  const [vehicles, setVehicles] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [repairRequests, setRepairRequests] = useState([]);
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [vehiclesRes, repairsRes, requestsRes, maintenanceRes] = await Promise.all([
        axios.get(`${url}/api/equipment/listEquipment?forFleetOverview=true`),
        axios.get(`${url}/api/equipment/getRepairs`),
        axios.get(`${url}/api/equipment/repairRequests`),
        axios.get(`${url}/api/equipment/getMaintenanceHistory`)
      ]);

      setVehicles(vehiclesRes.data.equipment || []);
      setRepairs(repairsRes.data || []);
      setRepairRequests(requestsRes.data.data || []);
      setMaintenanceHistory(maintenanceRes.data || []);
    } catch (error) {
      console.error('Error fetching fleet data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getVehicleRepairs = (vehicleId) => {
    return repairs.filter(r => r.equipmentId?._id === vehicleId || r.equipmentId === vehicleId);
  };

  const getVehicleRepairRequests = (vehicleId) => {
    return repairRequests.filter(r => 
      (r.equipmentId?._id === vehicleId || r.equipmentId === vehicleId) && 
      r.status !== 'Converted' && 
      r.status !== 'Rejected'
    );
  };

  const getVehicleMaintenanceHistory = (vehicleId) => {
    return maintenanceHistory.filter(m => 
      m.vehicleId === vehicleId || m.equipmentId === vehicleId
    );
  };

  const getOpenRepairsCount = (vehicleId) => {
    return getVehicleRepairs(vehicleId).filter(r => r.status !== 'Completed').length;
  };

  const getMaintenanceStatus = (vehicle) => {
    if (!vehicle.mileage || !vehicle.nextServiceMileage) return 'unknown';
    const milesUntilService = vehicle.nextServiceMileage - vehicle.mileage;
    
    if (milesUntilService < 0) return 'overdue';
    if (milesUntilService < 500) return 'due-soon';
    return 'good';
  };

  const getMaintenanceChip = (vehicle) => {
    const status = getMaintenanceStatus(vehicle);
    const milesUntilService = vehicle.nextServiceMileage - vehicle.mileage;
    
    switch (status) {
      case 'overdue':
        return (
          <Chip
            size="small"
            label={`Overdue by ${Math.abs(milesUntilService)} mi`}
            className={`${classes.statusChip} ${classes.criticalStatus}`}
            icon={<ErrorIcon />}
          />
        );
      case 'due-soon':
        return (
          <Chip
            size="small"
            label={`Due in ${milesUntilService} mi`}
            className={`${classes.statusChip} ${classes.warningStatus}`}
            icon={<WarningIcon />}
          />
        );
      case 'good':
        return (
          <Chip
            size="small"
            label={`OK (${milesUntilService} mi)`}
            className={`${classes.statusChip} ${classes.goodStatus}`}
            icon={<CheckCircleIcon />}
          />
        );
      default:
        return <Chip size="small" label="No data" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy');
    } catch {
      return 'N/A';
    }
  };

  const handleRowExpand = (vehicleId) => {
    setExpandedRows(prev => ({
      ...prev,
      [vehicleId]: !prev[vehicleId]
    }));
  };

  const handleViewDetails = (vehicle) => {
    setSelectedVehicle(vehicle);
    setDetailDialogOpen(true);
    setTabValue(0);
  };

  const calculateTotalRepairCost = (vehicleId) => {
    return getVehicleRepairs(vehicleId)
      .reduce((total, repair) => total + (repair.cost || 0), 0);
  };

  // Priority sorting function
  const getVehiclePriority = (vehicle) => {
    const maintenanceStatus = getMaintenanceStatus(vehicle);
    const openRepairRequests = getVehicleRepairRequests(vehicle._id).length;
    const openRepairs = getOpenRepairsCount(vehicle._id);
    
    // Priority scoring:
    // - Overdue maintenance: 1000 points
    // - Each open repair request: 100 points
    // - Each open repair: 10 points
    // - Due soon maintenance: 50 points
    
    let priority = 0;
    
    if (maintenanceStatus === 'overdue') {
      priority += 1000;
    } else if (maintenanceStatus === 'due-soon') {
      priority += 50;
    }
    
    priority += openRepairRequests * 100;
    priority += openRepairs * 10;
    
    return priority;
  };

  // Filter and sort vehicles
  const filteredAndSortedVehicles = vehicles
    ?.filter(vehicle =>
      vehicle.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.number?.toString().includes(searchTerm) ||
      vehicle.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    ?.sort((a, b) => {
      // Sort by priority (higher priority first)
      return getVehiclePriority(b) - getVehiclePriority(a);
    });

  // Calculate statistics
  const stats = {
    totalVehicles: vehicles?.length,
    activeVehicles: vehicles?.filter(v => (v.isActive && !getVehicleRepairs(v._id).some(r => r.outOfService && r.status !== 'Completed'))).length,
    maintenanceDue: vehicles?.filter(v => getMaintenanceStatus(v) === 'due-soon' || getMaintenanceStatus(v) === 'overdue').length,
    openRepairs: repairs.filter(r => r.status !== 'Completed').length,
    openRepairRequests: repairRequests.filter(r => r.status !== 'Converted' && r.status !== 'Rejected').length,
  };

  const VehicleIcon = ({ type }) => {
    return type?.toLowerCase() === 'truck' ? 
      <TruckIcon className={classes.vehicleIcon} /> : 
      <CarIcon className={classes.vehicleIcon} />;
  };

  // Check if vehicle should be highlighted
  const shouldHighlightRow = (vehicle) => {
    const maintenanceStatus = getMaintenanceStatus(vehicle);
    const hasOpenRepairRequests = getVehicleRepairRequests(vehicle._id).length > 0;
    const hasOpenRepairs = getOpenRepairsCount(vehicle._id) > 0;
    
    return maintenanceStatus === 'overdue' || hasOpenRepairRequests || hasOpenRepairs;
  };

  return (
    <Container className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Fleet Overview
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={fetchAllData}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.cardContent}>
              <div>
                <Typography color="textSecondary" gutterBottom>
                  Total Vehicles
                </Typography>
                <Typography variant="h4">
                  {stats.totalVehicles} 
                </Typography>
                <div className={classes.detailsText}>
                    {stats.activeVehicles} Active
                </div>
              </div>
              <CarIcon className={classes.statsIcon} color="primary" />
            </CardContent>
          </Card>
        </Grid>
        
        {/* <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.cardContent}>
              <div>
                <Typography color="textSecondary" gutterBottom>
                  Active Vehicles
                </Typography>
                <Typography variant="h4">
                  {stats.activeVehicles}
                </Typography>
              </div>
              <CheckCircleIcon className={classes.statsIcon} style={{ color: '#4CAF50' }} />
            </CardContent>
          </Card>
        </Grid> */}

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.cardContent}>
              <div>
                <Typography color="textSecondary" gutterBottom>
                  Maintenance Due
                </Typography>
                <Typography variant="h4">
                  {stats.maintenanceDue}
                </Typography>
              </div>
              <ScheduleIcon className={classes.statsIcon} style={{ color: '#FF9800' }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.cardContent}>
              <div>
                <Typography color="textSecondary" gutterBottom>
                  Open Repairs
                </Typography>
                <Typography variant="h4">
                  {stats.openRepairs}
                </Typography>
              </div>
              <BuildIcon className={classes.statsIcon} style={{ color: '#2196F3' }} />
            </CardContent>
          </Card>
        </Grid>

        {/* New Open Repair Requests Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <CardContent className={classes.cardContent}>
              <div>
                <Typography color="textSecondary" gutterBottom>
                  Open Repair Requests
                </Typography>
                <Typography variant="h4">
                  {stats.openRepairRequests}
                </Typography>
              </div>
              <AssignmentIcon className={classes.statsIcon} style={{ color: '#9C27B0' }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Paper className={classes.searchContainer}>
        <TextField
          className={classes.searchField}
          variant="outlined"
          placeholder="Search by name, number, make, or model..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          style={{ width: '100%' }}
        />
      </Paper>

      {/* Vehicles Table */}
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : filteredAndSortedVehicles.length === 0 ? (
        <Paper className={classes.emptyState}>
          <Typography variant="h6">
            No vehicles found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Vehicle</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Year/Make/Model</TableCell>
                <TableCell align="center">Mileage</TableCell>
                <TableCell align="center">Maintenance</TableCell>
                <TableCell align="center">Open Repairs</TableCell>
                <TableCell align="center">Repair Requests</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedVehicles.map((vehicle) => (
                <React.Fragment key={vehicle._id}>
                  <TableRow 
                    className={`${classes.vehicleRow} ${shouldHighlightRow(vehicle) ? classes.priorityRow : ''}`}
                  >
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRowExpand(vehicle._id)}
                      >
                        {expandedRows[vehicle._id] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <strong>{vehicle.name} #{vehicle.number}</strong>
                    </TableCell>
                    <TableCell>{vehicle.type}</TableCell>
                    <TableCell>
                      {vehicle.year} {vehicle.manufacturer} {vehicle.model}
                    </TableCell>
                    <TableCell align="center">
                      {vehicle.mileage ? vehicle.mileage.toLocaleString() : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {getMaintenanceChip(vehicle)}
                    </TableCell>
                    <TableCell align="center">
                      <Badge
                        badgeContent={getOpenRepairsCount(vehicle._id)}
                        color="primary"
                        className={getOpenRepairsCount(vehicle._id) > 0 ? classes.urgentBadge : ''}
                      >
                        <BuildIcon />
                      </Badge>
                    </TableCell>
                    <TableCell align="center">
                      <Badge
                        badgeContent={getVehicleRepairRequests(vehicle._id).length}
                        color="secondary"
                      >
                        <AssignmentIcon />
                      </Badge>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(vehicle)}
                        >
                          <Pageview />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.expandCell} colSpan={9}>
                      <Collapse in={expandedRows[vehicle._id]} timeout="auto" unmountOnExit>
                        <Box className={classes.expandedContent} p={3}>
                          <Grid container spacing={3}>
                            {/* Quick Info */}
                            <Grid item xs={12} md={4}>
                              <Typography className={classes.sectionTitle}>
                                Vehicle Information
                              </Typography>
                              <Typography variant="body2">
                                <strong>VIN:</strong> {vehicle.vin || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>License Plate:</strong> {vehicle.licensePlate || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Division:</strong> {vehicle.division || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Assigned Driver:</strong> {vehicle.assignedDriverName || 'Unassigned'}
                              </Typography>
                            </Grid>

                            {/* Recent Repairs */}
                            <Grid item xs={12} md={4}>
                              <Typography className={classes.sectionTitle}>
                                Recent Repairs
                              </Typography>
                              {getVehicleRepairs(vehicle._id).slice(0, 3).length > 0 ? (
                                <List dense>
                                  {getVehicleRepairs(vehicle._id).slice(0, 3).map((repair, idx) => (
                                    <ListItem key={idx} className={classes.listItem}>
                                      <ListItemIcon>
                                        <BuildIcon fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={repair.description}
                                        secondary={`${formatDate(repair.date)} - ${repair.status}`}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              ) : (
                                <Typography variant="body2" className={classes.noData}>
                                  No recent repairs
                                </Typography>
                              )}
                            </Grid>

                            {/* Maintenance Progress */}
                            <Grid item xs={12} md={4}>
                              <Typography className={classes.sectionTitle}>
                                Maintenance Progress
                              </Typography>
                              {vehicle.mileage && vehicle.nextServiceMileage ? (
                                <>
                                  <Typography variant="body2">
                                    Current: {vehicle.mileage.toLocaleString()} mi
                                  </Typography>
                                  <Typography variant="body2">
                                    Next Service: {vehicle.nextServiceMileage.toLocaleString()} mi
                                  </Typography>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, (vehicle.mileage / vehicle.nextServiceMileage) * 100)}
                                    className={classes.mileageProgress}
                                    color={getMaintenanceStatus(vehicle) === 'overdue' ? 'secondary' : 'primary'}
                                  />
                                </>
                              ) : (
                                <Typography variant="body2" className={classes.noData}>
                                  No maintenance data available
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedVehicle && (
            <Box className={classes.dialogHeader}>
              <VehicleIcon type={selectedVehicle.type} />
              <div>
                <Typography variant="h6">
                  <Link 
                    style={{pointer: 'cursor', color: '#516876', fontWeight: 600, fontSize: 18 }}
                    target="_blank"
                    to={{ 
                      pathname: `/equipment/details/${selectedVehicle._id}`,
                                // search: '?sort=name',
                                // hash: '#the-hash',
                                state: {item: selectedVehicle, stuff: 'Search Engine'}
                     }}
                    
                  >{selectedVehicle.name} #{selectedVehicle.number} </Link>
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {selectedVehicle.year} {selectedVehicle.manufacturer} {selectedVehicle.model}
                </Typography>
              </div>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedVehicle && (
            <>
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="Vehicle Info" />
                <Tab label="Repair History" />
                <Tab label="Maintenance History" />
                <Tab label="Repair Requests" />
              </Tabs>

              {/* Vehicle Info Tab */}
              {tabValue === 0 && (
                <Box className={classes.tabContent}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">VIN</Typography>
                      <Typography variant="body1">{selectedVehicle.vin || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">License Plate</Typography>
                      <Typography variant="body1">{selectedVehicle.licensePlate || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Current Mileage</Typography>
                      <Typography variant="body1">
                        {selectedVehicle.mileage ? selectedVehicle.mileage.toLocaleString() : 'N/A'} miles
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Next Service</Typography>
                      <Typography variant="body1">
                        {selectedVehicle.nextServiceMileage ? selectedVehicle.nextServiceMileage.toLocaleString() : 'N/A'} miles
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Division</Typography>
                      <Typography variant="body1">{selectedVehicle.division || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Assigned Driver</Typography>
                      <Typography variant="body1">{selectedVehicle.assignedDriverName || 'Unassigned'}</Typography>
                    </Grid>
                  </Grid>
                  
                  <Box className={classes.costSummary}>
                    <Typography variant="h6" gutterBottom>
                      <MoneyIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                      Cost Summary
                    </Typography>
                    <Typography variant="body1">
                      <strong>Total Repair Costs:</strong> ${calculateTotalRepairCost(selectedVehicle._id).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Based on {getVehicleRepairs(selectedVehicle._id).length} repairs
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Repair History Tab */}
              {tabValue === 1 && (
                <Box className={classes.tabContent}>
                  {getVehicleRepairs(selectedVehicle._id).length > 0 ? (
                    <List>
                      {getVehicleRepairs(selectedVehicle._id).map((repair, idx) => (
                        <ListItem key={idx} className={classes.listItem}>
                          <ListItemIcon>
                            <BuildIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={repair.description}
                            secondary={
                              <span>
                                {formatDate(repair.date)} | Status: {repair.status} | 
                                Cost: ${repair.cost || 0} | Priority: {repair.priority}
                              </span>
                            }
                          />
                          {repair.outOfService && (
                            <Chip
                              size="small"
                              label="OOS"
                              color="secondary"
                            />
                          )}
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography className={classes.noData}>
                      No repair history available
                    </Typography>
                  )}
                </Box>
              )}

              {/* Maintenance History Tab */}
              {tabValue === 2 && (
                <Box className={classes.tabContent}>
                  {getVehicleMaintenanceHistory(selectedVehicle._id).length > 0 ? (
                    <List>
                      {getVehicleMaintenanceHistory(selectedVehicle._id).reverse().map((maintenance, idx) => (
                        <ListItem key={idx} className={classes.listItem}>
                          <ListItemIcon>
                            <ScheduleIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Service at ${maintenance.currentMileage?.toLocaleString() || 'N/A'} miles`}
                            secondary={
                              <span>
                                {formatDate(maintenance.serviceDate)} | 
                                Next: {maintenance.nextServiceMileage?.toLocaleString() || 'N/A'} miles
                              </span>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography className={classes.noData}>
                      No maintenance history available
                    </Typography>
                  )}
                </Box>
              )}

              {/* Repair Requests Tab */}
              {tabValue === 3 && (
                <Box className={classes.tabContent}>
                  {getVehicleRepairRequests(selectedVehicle._id).length > 0 ? (
                    <List>
                      {getVehicleRepairRequests(selectedVehicle._id).reverse().map((request, idx) => (
                        <ListItem key={idx} className={classes.listItem}>
                          <ListItemIcon>
                            <AssignmentIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={request.reportedIssues.map(i => i.name).join(', ')}
                            secondary={
                              <span>
                                {formatDate(request.created_at)} | 
                                Status: {request.status} | 
                                Reported by: {request.reportedByName}
                              </span>
                            }
                          />
                          <Chip
                            size="small"
                            label={request.status}
                            color={request.status === 'Under Review' ? 'primary' : 'default'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography className={classes.noData}>
                      No open repair requests
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
});

export default connect(mapStateToProps)(FleetOverview);
